// TanStack Query hooks for the Assets & Equipment module.
// Mirrors packages/api/src/routes/assets.ts endpoint shapes and imports the
// wire types from @dsr/shared-types. All write hooks invalidate the relevant
// query keys so list / detail views stay fresh after a mutation.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Asset,
  AssetResponse,
  AssetsListResponse,
  AssetStatus,
  AssetType,
  AssetAssignment,
  AssetAssignmentsListResponse,
  CheckinAssetInput,
  CheckoutAssetInput,
  CreateAssetInput,
  CreateMaintenanceRecordInput,
  MaintenanceRecord,
  MaintenanceRecordResponse,
  MaintenanceRecordsListResponse,
  TransferAssetInput,
  UpdateAssetInput,
} from '@dsr/shared-types';
import { api } from '../client';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const assetKeys = {
  all: ['assets'] as const,
  list: (filters: AssetFilters) => ['assets', { ...filters }] as const,
  detail: (id: number) => ['assets', id] as const,
  assignments: (id: number) => ['assets', id, 'assignments'] as const,
  maintenance: (id: number) => ['assets', id, 'maintenance'] as const,
};

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export interface AssetFilters {
  asset_type?: AssetType;
  status?: AssetStatus;
  assigned_site_id?: number;
}

export function useAssets(filters: AssetFilters = {}) {
  const params = new URLSearchParams();
  if (filters.asset_type) params.set('asset_type', filters.asset_type);
  if (filters.status) params.set('status', filters.status);
  if (filters.assigned_site_id !== undefined) {
    params.set('assigned_site_id', String(filters.assigned_site_id));
  }
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/assets?${qs}` : '/api/assets';
  return useQuery({
    queryKey: assetKeys.list(filters),
    queryFn: ({ signal }) => api.get<AssetsListResponse>(url, signal),
  });
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

export function useAsset(id: number | null) {
  return useQuery({
    queryKey: assetKeys.detail(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<AssetResponse>(`/api/assets/${id}`, signal),
  });
}

// Custody timeline (append-only asset_assignments, newest first).
export function useAssetAssignments(id: number | null) {
  return useQuery({
    queryKey: assetKeys.assignments(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<AssetAssignmentsListResponse>(`/api/assets/${id}/assignments`, signal),
  });
}

// Maintenance history (audited maintenance_records).
export function useAssetMaintenance(id: number | null) {
  return useQuery({
    queryKey: assetKeys.maintenance(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<MaintenanceRecordsListResponse>(`/api/assets/${id}/maintenance`, signal),
  });
}

// ---------------------------------------------------------------------------
// Create / update / delete
// ---------------------------------------------------------------------------

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAssetInput) =>
      api.post<AssetResponse>('/api/assets', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetKeys.all });
    },
  });
}

export function useUpdateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number } & UpdateAssetInput) =>
      api.patch<AssetResponse>(`/api/assets/${id}`, patch),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: assetKeys.all });
      void qc.invalidateQueries({ queryKey: assetKeys.detail(vars.id) });
    },
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/assets/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Custody events. Each returns the updated asset + the appended custody row,
// so we invalidate the asset list, the detail row, and its custody timeline.
// ---------------------------------------------------------------------------

interface CustodyResult {
  asset: Asset;
  assignment: AssetAssignment;
}

function invalidateCustody(qc: ReturnType<typeof useQueryClient>, id: number): void {
  void qc.invalidateQueries({ queryKey: assetKeys.all });
  void qc.invalidateQueries({ queryKey: assetKeys.detail(id) });
  void qc.invalidateQueries({ queryKey: assetKeys.assignments(id) });
}

export function useCheckoutAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & CheckoutAssetInput) =>
      api.post<CustodyResult>(`/api/assets/${id}/checkout`, body),
    onSuccess: (_data, vars) => invalidateCustody(qc, vars.id),
  });
}

export function useCheckinAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & CheckinAssetInput) =>
      api.post<CustodyResult>(`/api/assets/${id}/checkin`, body),
    onSuccess: (_data, vars) => invalidateCustody(qc, vars.id),
  });
}

export function useTransferAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & TransferAssetInput) =>
      api.post<CustodyResult>(`/api/assets/${id}/transfer`, body),
    onSuccess: (_data, vars) => invalidateCustody(qc, vars.id),
  });
}

// ---------------------------------------------------------------------------
// Maintenance
// ---------------------------------------------------------------------------

export function useCreateMaintenanceRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & CreateMaintenanceRecordInput) =>
      api.post<MaintenanceRecordResponse>(`/api/assets/${id}/maintenance`, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: assetKeys.all });
      void qc.invalidateQueries({ queryKey: assetKeys.detail(vars.id) });
      void qc.invalidateQueries({ queryKey: assetKeys.maintenance(vars.id) });
    },
  });
}

// Re-export the wire types the pages consume so they can import everything
// asset-related from this module if they prefer.
export type {
  Asset,
  AssetAssignment,
  AssetStatus,
  AssetType,
  MaintenanceRecord,
};
